import { useState, useMemo, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Button, Notification, PageView, PageTopBar } from '../../components'
import { Loader } from '../../components/Loader'
import { fetchWarehouse } from '../../services/warehouseSevices'
import { fetchWarehouseInboundAppointments, fetchReceivingFranjas, fetchInboundFranjaSummary, fetchInboundWeekOverviewDays } from '../../services'
import { todayInTimezone, weekDatesFrom } from '../../utils/warehouseDates'
import { translateAppointmentError, isArrivalAppointment, buildWeekOverview, isCapacitySummaryAbsent } from '../../utils/inboundAppointments'
import { WeekOverviewGrid } from './WeekOverviewGrid'
import { resolveReceptionPath } from './receptionPath'
import { AppointmentsBoard } from './AppointmentsBoard'
import { FIVE_MINUTES_MS } from '../../helpers/cacheTimes'

const T = 'replenishments.inbound_appointments.board'
const STATUS_OPTIONS = ['booked', 'checked_in', 'canceled', 'no_show', 'superseded']
const CLASS_OPTIONS = ['standard', 'extemporaneous']

// Shared amber notice strip — week stale-refetch, week capacity-degraded, and
// day capacity-degraded all render the identical message+retry affordance.
// One owner of the markup so a visual tweak never has to land 3x.
const AmberNotice = ({ message, retryLabel, onRetry }) => (
  <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
    <span>{message}</span>
    <button type="button" className="underline font-medium" onClick={onRetry}>
      {retryLabel}
    </button>
  </div>
)

// Single-date ops board (contract §4.3). Route-gated on
// complete_replenishment (RouterConfig), the same grant the backend policy
// checks — no extra permission handling here.
export const InboundAppointmentsBoardContainer = () => {
  const { i18n } = useTranslation()
  const { warehouse_id } = useParams()
  const history = useHistory()

  // State holds only explicit user picks; the default date is DERIVED from
  // the warehouse timezone below. Never seed it from the query's onSuccess —
  // react-query v3 does not fire onSuccess when fresh CACHED data resolves
  // the query (the warehouse is always cached right after a reception-screen
  // visit), which would strand this page on a permanent spinner (D16).
  const [pickedDate, setPickedDate] = useState(null)
  // 'arrivals' (default, label "Llegadas"): client-side booked+checked_in+
  // no_show — operators want a quick glance at real arrivals; history stays
  // one select away. 'all' (label "Todas") and single statuses behave as
  // before. English sentinels match the status-value namespace.
  const [statusFilter, setStatusFilter] = useState('arrivals')
  const [classFilter, setClassFilter] = useState('all')
  // 'day' (default — the ops board is unchanged) | 'week' (7-day compilation
  // grid, spec 2026-08-18 week-overview). The date input doubles as the week
  // window start in week mode.
  const [viewMode, setViewMode] = useState('day')
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const [openingAppointmentId, setOpeningAppointmentId] = useState(null)

  const {
    data: warehouse,
    isError: warehouseIsError,
    refetch: refetchWarehouse,
  } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: false,
    enabled: !!warehouse_id,
  })

  const date = pickedDate ?? (warehouse ? todayInTimezone(warehouse.time_zone) : null)

  const {
    data: franjas,
    isError: franjasIsError,
    refetch: refetchFranjas,
  } = useQuery(['receiving_franjas', warehouse_id], () => fetchReceivingFranjas(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: false,
    enabled: !!warehouse_id,
  })

  const isAggregateStatusView = statusFilter === 'arrivals' || statusFilter === 'all'
  const params = useMemo(() => (isAggregateStatusView ? { date } : { date, status: statusFilter }), [date, isAggregateStatusView, statusFilter])

  // refetchInterval: dock boards get left open all morning — poll gently so
  // new check-ins and walk-ins appear without a manual refresh (D25).
  const {
    data: appointments,
    isLoading,
    isError,
    refetch,
  } = useQuery(['inbound_appointments', warehouse_id, params], () => fetchWarehouseInboundAppointments(warehouse_id, params), {
    enabled: !!warehouse_id && !!date && viewMode === 'day',
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchInterval: 60000,
  })

  // Capacity strip data (#4229). Asymmetric failure posture (spec §3):
  // 404 → platform not deployed yet — feature absent, silent.
  // 403 → grant problem the appointments fetch surfaces loudly anyway.
  // Anything else with NO data to show → one-line notice; the board itself
  // must never be taken down by supplementary capacity data. A transient
  // poll failure AFTER a success keeps the last strips (react-query retains
  // data on error) and stays quiet.
  const {
    data: franjaSummary,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery(['inbound_franja_summary', warehouse_id, date], () => fetchInboundFranjaSummary(warehouse_id, { date }), {
    enabled: !!warehouse_id && !!date && viewMode === 'day',
    // No per-fetch retries: the 60s poll IS the retry, and until #4229
    // deploys every attempt is a known 404 — backoff retries would just
    // hammer it (final-review finding).
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60000,
  })
  const showSummaryError = !!summaryError && !franjaSummary && !isCapacitySummaryAbsent(summaryError)

  // Week overview (spec 2026-08-18 week-overview §4): ONE query for the whole
  // window — a single loading/error/retry state instead of 14. Planning view,
  // not live dock watching: a gentle 5-minute poll (vs the day board's 60s).
  // retry: 1 — each attempt is secretly 14 requests, so default retries would
  // cost 56 requests per cycle against a down platform; one retry shields a
  // transient blip and the poll is the real retry (eng-review D8, same
  // rationale as the summary poll above).
  const weekQueryEnabled = viewMode === 'week' && !!warehouse_id && !!date
  const {
    data: weekDays,
    isLoading: weekIsLoading,
    isFetching: weekIsFetching,
    isError: weekIsError,
    isPreviousData: weekIsPreviousData,
    refetch: refetchWeek,
  } = useQuery(['inbound_week_overview', warehouse_id, date], () => fetchInboundWeekOverviewDays(warehouse_id, weekDatesFrom(date)), {
    enabled: weekQueryEnabled,
    keepPreviousData: true,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: FIVE_MINUTES_MS,
  })
  // A window change picks a brand-new query key (the date is part of it), so
  // that fetch starts with no data of its own. react-query's keepPreviousData
  // explicitly withholds its placeholder once the new key's status is
  // 'error' (queryObserver: `status !== 'error'`) — so `weekDays` reverts to
  // undefined right when the fetch fails, not the prior window. Cache the
  // last successful window ourselves so a failed refetch can fall back to it
  // instead of yanking the grid out from under the stale-notice banner (OV#4).
  // Scoped by warehouseId (checklist + adversarial cross-confirmed): an
  // unscoped cache could render another warehouse's dock data under the
  // stale banner after a switch, since a route param change alone never
  // remounts this component. The cache still ignores the DATE on purpose —
  // it is the last successful window for the CURRENT warehouse regardless of
  // which window that was (D10): a failed window-change refetch must keep
  // showing the previous window's grid with the stale banner.
  // !weekIsPreviousData guards the write itself: keepPreviousData serves the
  // PRIOR key's data as a placeholder (with the CURRENT warehouse_id already
  // in scope) while a new key is in flight — caching that would silently
  // relabel warehouse 1's grid as warehouse 2's the moment the route
  // switches, defeating the scoping above before the fetch even settles.
  const [cachedWeekDays, setCachedWeekDays] = useState(null)
  useEffect(() => {
    if (weekDays && !weekIsPreviousData) setCachedWeekDays({ warehouseId: warehouse_id, days: weekDays })
  }, [weekDays, weekIsPreviousData, warehouse_id])
  const warehouseCachedWeekDays = cachedWeekDays?.warehouseId === warehouse_id ? cachedWeekDays.days : null
  const weekOverview = useMemo(() => {
    const days = weekDays || warehouseCachedWeekDays
    return days ? buildWeekOverview(days, franjas || [], warehouse?.time_zone) : null
  }, [weekDays, warehouseCachedWeekDays, franjas, warehouse?.time_zone])

  // Drill-down contract (OV#3/D9): the clicked cell promised Llegadas
  // numbers, so the landing day board must show them — reset any leftover
  // history filters. Returning to Semana afterwards re-anchors the window on
  // this date (accepted, OV#5/D11): one date state, one-sentence behavior.
  const handleSelectDate = selectedDate => {
    setPickedDate(selectedDate)
    setStatusFilter('arrivals')
    setClassFilter('all')
    setViewMode('day')
  }

  // Class filtering is client-side (the endpoint only filters by status), and
  // so is the default Llegadas view — the fetch stays the full day list so
  // switching Llegadas ⇄ Todas never refetches.
  const visibleAppointments = useMemo(() => {
    const list = appointments || []
    const byClass = classFilter === 'all' ? list : list.filter(a => a.appointment_class === classFilter)
    return statusFilter === 'arrivals' ? byClass.filter(isArrivalAppointment) : byClass
  }, [appointments, classFilter, statusFilter])

  // "Filters emptied it" copy (spec §4): explicit narrowing, or the default
  // Llegadas view actually hiding rows. Llegadas with nothing hidden is NOT
  // an active filter — an empty day still reads as an empty day.
  const arrivalsHidRows = statusFilter === 'arrivals' && (appointments?.length || 0) > visibleAppointments.length
  const filtersActive = classFilter !== 'all' || !isAggregateStatusView || arrivalsHidRows

  // A failed flow-lookup must never be a silent dead click (6A); the row
  // link disables while the lookup is in flight (D24).
  const handleOpenReception = async appointment => {
    setOpeningAppointmentId(appointment.id)
    try {
      const path = await resolveReceptionPath(appointment.replenishment_id, warehouse_id)
      history.push(path)
    } catch (error) {
      setNotification({ show: true, type: 'error', title: translateAppointmentError(i18n, error) })
    } finally {
      setOpeningAppointmentId(null)
    }
  }

  const selectClasses = 'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">{i18n.t(`${T}.title`)}</div>
          <div className="flex items-center gap-2 ml-auto">
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm" role="group">
              <button
                type="button"
                className={`px-3 py-2 font-medium ${viewMode === 'week' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
                onClick={() => setViewMode('week')}
                aria-pressed={viewMode === 'week'}
              >
                {i18n.t(`${T}.week.toggle_week`)}
              </button>
              <button
                type="button"
                className={`px-3 py-2 font-medium ${viewMode === 'day' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
                onClick={() => setViewMode('day')}
                aria-pressed={viewMode === 'day'}
              >
                {i18n.t(`${T}.week.toggle_day`)}
              </button>
            </div>
            <input
              type="date"
              className={selectClasses}
              value={date || ''}
              onChange={e => setPickedDate(e.target.value || null)}
              aria-label={i18n.t(`${T}.date_label`)}
            />
            {viewMode === 'day' && (
              <>
                <select className={selectClasses} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label={i18n.t(`${T}.status_label`)}>
                  <option value="arrivals">{i18n.t(`${T}.arrivals_filter`)}</option>
                  <option value="all">{i18n.t(`${T}.all`)}</option>
                  {STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>
                      {i18n.t(`replenishments.inbound_appointments.statuses.${status}`)}
                    </option>
                  ))}
                </select>
                <select className={selectClasses} value={classFilter} onChange={e => setClassFilter(e.target.value)} aria-label={i18n.t(`${T}.class_label`)}>
                  <option value="all">{i18n.t(`${T}.all`)}</option>
                  {CLASS_OPTIONS.map(klass => (
                    <option key={klass} value={klass}>
                      {i18n.t(`replenishments.inbound_appointments.classes.${klass}`)}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <Notification
        show={notification.show}
        setShow={val => setNotification(prev => ({ ...prev, show: val }))}
        type={notification.type}
        title={notification.title}
      />

      {warehouseIsError ? (
        // The date derives from the warehouse query — its failure must never
        // strand the page on a permanent spinner (final-review D8).
        <div className="text-center py-12">
          <p className="text-red-600 font-medium mb-3">{i18n.t(`${T}.load_error`)}</p>
          <Button type="secondary" onClick={() => refetchWarehouse()}>
            {i18n.t(`${T}.retry`)}
          </Button>
        </div>
      ) : franjasIsError ? (
        // Franja grouping — free windows included — is the board's point; a
        // failed catalog must not impersonate a franja-less day (4A).
        <div className="text-center py-12">
          <p className="text-red-600 font-medium mb-3">{i18n.t(`${T}.franjas_load_error`)}</p>
          <Button type="secondary" onClick={() => refetchFranjas()}>
            {i18n.t(`${T}.retry`)}
          </Button>
        </div>
      ) : viewMode === 'week' ? (
        weekIsLoading || !date ? (
          <Loader show={true} />
        ) : weekIsError && !weekDays && !warehouseCachedWeekDays ? (
          // Nothing to show at all: a broken week must never impersonate a
          // quiet week (4A). With cached data present we fall through below —
          // a failed background refetch must not yank a readable grid (OV#4/D10).
          <div className="text-center py-12">
            <p className="text-red-600 font-medium mb-3">{i18n.t(`${T}.week.load_error`)}</p>
            <Button type="secondary" onClick={() => refetchWeek()}>
              {i18n.t(`${T}.retry`)}
            </Button>
          </div>
        ) : (
          <>
            {weekIsError && <AmberNotice message={i18n.t(`${T}.week.stale_error`)} retryLabel={i18n.t(`${T}.retry`)} onRetry={() => refetchWeek()} />}
            {weekOverview?.capacityDegraded && (
              <AmberNotice message={i18n.t(`${T}.capacity_error`)} retryLabel={i18n.t(`${T}.retry`)} onRetry={() => refetchWeek()} />
            )}
            {weekOverview && (
              // Dim, don't hide, the previous window while the next one loads
              // (OV#6/D12) — instant feedback with zero layout shift.
              <div className={weekIsFetching ? 'opacity-50 pointer-events-none transition-opacity' : ''} aria-busy={weekIsFetching}>
                <WeekOverviewGrid overview={weekOverview} todayDate={warehouse ? todayInTimezone(warehouse.time_zone) : null} onSelectDate={handleSelectDate} />
              </div>
            )}
          </>
        )
      ) : isLoading || !date ? (
        <Loader show={true} />
      ) : isError ? (
        // A broken board must never impersonate an empty day (4A).
        <div className="text-center py-12">
          <p className="text-red-600 font-medium mb-3">{i18n.t(`${T}.load_error`)}</p>
          <Button type="secondary" onClick={() => refetch()}>
            {i18n.t(`${T}.retry`)}
          </Button>
        </div>
      ) : (
        <>
          {showSummaryError && <AmberNotice message={i18n.t(`${T}.capacity_error`)} retryLabel={i18n.t(`${T}.retry`)} onRetry={() => refetchSummary()} />}
          <AppointmentsBoard
            appointments={visibleAppointments}
            franjas={franjas || []}
            franjaSummary={franjaSummary || null}
            timeZone={warehouse?.time_zone}
            onOpenReception={handleOpenReception}
            openingAppointmentId={openingAppointmentId}
            filtersActive={filtersActive}
          />
        </>
      )}
    </PageView>
  )
}
